import { Col, Form, Row } from 'antd'
import moment from 'moment'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import Picker from 'react-mobile-picker'
import styled from 'styled-components'

import { InputField } from 'src/components/form'
import {
  InputLabel,
  LabelRequire,
} from 'src/components/layout/Header/HeaderStyle'
import BirthDateAGenderModal from 'src/components/Modal/BirthDateAGenderModal'
import BirthDateModal from 'src/components/Modal/BirthDateModal'
import GenderModal from 'src/components/Modal/GenderModal'
import { UserRequestInterface } from 'src/interfaces/user'
import { months, years } from 'src/utils/helper'
import femaleIcon from 'src/assets/icons/female.svg'
import maleIcon from 'src/assets/icons/male.svg'
import { USER_GENDER } from 'src/constants/user'
import { useForm, UseFormReturn } from 'react-hook-form'
import LocalStorage from 'src/utils/LocalStorage'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ButtonCustom from 'src/components/form/Button'
import { useRegister } from './useRegisterForm'
import addressRequest from 'src/api/requests/address'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import useCreateTreatment from 'src/hook/useCreateTreatment'
import { getMenuById } from 'src/useCase/question'
import { useAppDispatch } from 'src/store'
import { getClinicDetail } from 'src/useCase/clinic'
import DuplicateAccountModal from 'src/components/Modal/DuplicateAccountModal'

type Props = {
  flowName?: string | null
  form: UseFormReturn<UserRequestInterface>
  asForm?: boolean
}

type UserTreatmentFormValues = {
  menu_id: string | number | null
  plan_id?: string | number | null
}

const UserInfoForm = ({ flowName, form, asForm = true }: Props) => {
  const [selectGender, setSelectGender] = useState<string>()
  const [birthDatePopup, setBirthDatePopup] = useState<boolean>(false)
  const [genderPopup, setGenderPopup] = useState<boolean>(false)
  const [birthDateAGenderPopup, setBirthDateAGenderPopup] =
    useState<boolean>(false)
  const [isShowDatePicker, setIsShowDatePicker] = useState(false)
  const [birthDay, setBirthDay] = useState('1980-01-01')
  const { form: userForm, onSubmit: submitUserInfo } = useRegister()
  const navigate = useNavigate()
  const paramsUrl = useParams()
  const [searchParams] = useSearchParams()
  const menuId = searchParams.get('menuId') || paramsUrl?.id
  const planIdParam = searchParams.get('planId')
  const clinicId = searchParams.get('clinicId')
  const { createTreatment } = useCreateTreatment()
  const dispatch = useAppDispatch()
  const [targetGender, setTargetGender] = useState<string>()
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [isTelemedicine, setIsTelemedicine] = useState<boolean>(
    () => LocalStorage.getTelemedicine() === 'true',
  )
  const uuid = searchParams.get('uuid')
  const newForm = useMemo(() => {
    return isTelemedicine ? userForm : form
  }, [isTelemedicine, userForm, form])

  const Component = asForm ? Form : 'div'

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    trigger,
    setFocus,
    getValues,
  } = newForm

  const renderDays = (
    currentYear: string = '1980年',
    currentMonth: string = '1月',
  ) => {
    const lastDay = new Date(
      parseInt(currentYear),
      parseInt(currentMonth),
      0,
    ).getDate()

    return Array.from({ length: lastDay }, (_, index) => {
      return `${index + 1}日`
    })
  }

  const [valueGroups, setValueGroups] = useState({
    year: '1980年',
    month: '1月',
    day: '1日',
  })
  const [optionGroups, setOptionGroups] = useState({
    year: years(),
    month: months(),
    day: renderDays(valueGroups.year, valueGroups.month),
  })

  const userTreatmentForm = useForm<UserTreatmentFormValues>({
    defaultValues: {
      menu_id: null,
      plan_id: null,
    },
  })

  const handleBlur = async (field: 'phone' | 'email', value: string) => {
    if (!value) return

    if (newForm.formState.errors[field]) return

    try {
      await addressRequest.verifyUser({ [field]: value })
      newForm.clearErrors(field)
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.errors?.[field]?.[0] ||
        err?.response?.data?.message

      const isDuplicateError =
        apiMessage === 'この電話番号は既に使われています' ||
        apiMessage === 'このメールは既に使われています'

      if (isDuplicateError) {
        setIsDuplicateModalOpen(true)
      }

      newForm.setError(field, {
        type: 'manual',
        message: apiMessage,
      })
    }
  }

  const handleTelemedicineFlow = useCallback(async () => {
    if (!uuid) {
      return
    }

    try {
      const result = await dispatch(getClinicDetail({ uuid })).unwrap()
      const telemedicine = Boolean(result?.telemedicine_enabled)
      setIsTelemedicine(telemedicine)
      if (!telemedicine) {
        return navigate(
          `${AUTOMATIC_PLAN_PATH.QUESTIONS.replace(
            ':id',
            menuId || '',
          )}?${searchParams.toString()}`,
        )
      }
    } catch {
      return
    }
  }, [uuid, dispatch])

  useEffect(() => {
    handleTelemedicineFlow()
  }, [handleTelemedicineFlow])

  useEffect(() => {
    if (isTelemedicine && menuId && clinicId) {
      dispatch(
        getMenuById({
          menuId,
          params: {
            clinic_id: clinicId,
            select_field: 'target_gender',
          },
        }),
      )
        .then((res: any) => {
          const targetGender = res.payload.target_gender
          setTargetGender(`${targetGender}`)
        })
        .catch(() => '')
    }
  }, [isTelemedicine, menuId, clinicId])

  useEffect(() => {
    setOptionGroups({
      ...optionGroups,
      day: renderDays(valueGroups.year, valueGroups.month),
    })
  }, [valueGroups])

  useEffect(() => {
    const storedGender = LocalStorage.getLocalStorage('selected_gender')
    if (storedGender) {
      setSelectGender(String(storedGender))
      newForm.setValue('gender_id', String(storedGender), {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [newForm])

  const handleChangeDate = (name, value) => {
    setValueGroups({
      ...valueGroups,
      [name]: value,
    })
  }

  const showDatePicker = () => {
    setIsShowDatePicker(true)
    document.body.classList.add('stop-scroll')
  }

  const cancelDatePicker = () => {
    document.body.classList.remove('stop-scroll')
    setIsShowDatePicker(false)
  }

  const confirmBirthday = () => {
    document.body.classList.remove('stop-scroll')
    setIsShowDatePicker(false)
    const birthdayFormat = moment({
      year: parseInt(valueGroups.year),
      month: parseInt(valueGroups.month) - 1,
      day: parseInt(valueGroups.day),
    }).format('YYYY-MM-DD')
    setBirthDay(birthdayFormat)
    newForm.setValue('date_of_birth', birthdayFormat)
  }

  const setGender = (gender) => {
    setSelectGender(String(gender))
    newForm.setValue('gender_id', gender, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handleFormSubmit = async (data: UserRequestInterface) => {
    const valid = await trigger()
    if (!valid) {
      const err = newForm.formState.errors
      const firstErrName = Object.keys(err)[0] as keyof UserRequestInterface
      if (firstErrName) setFocus(firstErrName)
      return
    }

    const userData = getValues()
    const userGender = userData.gender_id

    const invalidGender = Boolean(
      Number(targetGender) &&
        Number(targetGender) !== 2 &&
        Number(targetGender) !== Number(userGender),
    )

    newForm.clearErrors('gender_id')

    if (invalidGender) {
      newForm.setError('gender_id', {
        type: 'manual',
        message: 'このメニューは選択した性別ではご利用できません。',
      })
      return
    }

    const ok = await submitUserInfo(userData, '', null)
    if (!ok) return

    const otpRes = await addressRequest.updatePhone({ phone: data.phone })

    const menuIdNum = menuId ? Number(menuId) : 0
    const planIdNum = planIdParam ? Number(planIdParam) : null

    const newUserTreatment = await createTreatment(
      menuIdNum,
      planIdNum,
      userTreatmentForm,
    )

    if (!newUserTreatment) return

    if (otpRes) {
      navigate(`${AUTOMATIC_PLAN_PATH.SMS_AUTH}?menu_id=${menuId}`, {
        state: { fromRegister: true },
      })
    }
  }

  return (
    <React.Fragment>
      <StyleForm as={Component}>
        <Row className="justify-content-between">
          <Col xs={11}>
            <InputLabel className="font-weight-bold">姓</InputLabel>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'family_name',
              }}
              inputProps={{
                placeholder: '入力してください',
              }}
            />
          </Col>
          <Col xs={11} className="ml-10">
            <Row className="justify-content-between">
              <InputLabel className="font-weight-bold">名</InputLabel>
              <LabelRequire>
                <span>必須</span>
              </LabelRequire>
            </Row>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'given_name',
              }}
              inputProps={{
                placeholder: '入力してください',
              }}
            />
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col xs={11}>
            <InputLabel className="font-weight-bold">姓(カナ)</InputLabel>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'family_name_kana',
              }}
              inputProps={{
                placeholder: '入力してください',
              }}
            />
          </Col>
          <Col xs={11} className="ml-10">
            <Row className="justify-content-between">
              <InputLabel className="font-weight-bold">名(カナ)</InputLabel>
              <LabelRequire>
                <span>必須</span>
              </LabelRequire>
            </Row>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'given_name_kana',
              }}
              inputProps={{
                placeholder: '入力してください',
              }}
            />
          </Col>
        </Row>

        <Col xs={24}>
          <Row className="justify-content-between">
            <InputLabel className="font-weight-bold">電話番号</InputLabel>
            <LabelRequire>
              <span>必須</span>
            </LabelRequire>
          </Row>
          <InputField<UserRequestInterface>
            controller={{
              control,
              name: 'phone',
            }}
            inputProps={{
              placeholder: '入力してください',
              minLength: 10,
              maxLength: 11,
              onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
                handleBlur('phone', e.target.value),
            }}
          />
        </Col>

        <Row className="justify-content-between">
          <Col xs={4}>
            <InputLabel className="font-weight-bold">注釈</InputLabel>
          </Col>
          <Col xs={17} className="ml-10">
            <Row className="justify-content-between">
              <span>
                問診の内容や、お薬服用後の状況によっては医師より直接ご連絡する場合がございます。
              </span>
            </Row>
          </Col>
        </Row>

        <Col xs={24} className="mt-20">
          <Row className="justify-content-between">
            <InputLabel className="font-weight-bold">メールアドレス</InputLabel>
            <LabelRequire>
              <span>必須</span>
            </LabelRequire>
          </Row>
          <InputField<UserRequestInterface>
            controller={{
              control,
              name: 'email',
            }}
            inputProps={{
              placeholder: '入力してください',
              type: 'email',
              onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
                handleBlur('email', e.target.value),
            }}
          />
        </Col>

        <Row>
          <Col xs={24}>
            <Row className="justify-content-between">
              <InputLabel className="font-weight-bold">生年月日</InputLabel>
              <LabelRequire>
                <span>必須</span>
              </LabelRequire>
            </Row>

            <div
              className="ant-form-item input-birthday"
              onClick={showDatePicker}
            >
              <p className="ant-input aga-input">{birthDay}</p>
            </div>
            {isShowDatePicker && (
              <div className="picker-group">
                <div className="picker-group__item">
                  <div className="d-flex justify-content-space-between birth-action">
                    <b onClick={cancelDatePicker} className="btn-gray">
                      キャンセル
                    </b>
                    <b className="btn-warning" onClick={confirmBirthday}>
                      OK
                    </b>
                  </div>
                  <div className="d-flex">
                    <Picker
                      optionGroups={optionGroups}
                      valueGroups={valueGroups}
                      onChange={handleChangeDate}
                      wheel="normal"
                    />
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Row className="justify-content-between">
              <InputLabel className="font-weight-bold">
                性別（どちらかを選択してください）
              </InputLabel>
              <LabelRequire>
                <span>必須</span>
              </LabelRequire>
            </Row>
            <Row className="justify-content-between">
              <Col xs={11}>
                <GenderItem
                  onClick={() => setGender(USER_GENDER.MALE)}
                  className={
                    selectGender === `${USER_GENDER.MALE}` ? `selected` : ''
                  }
                >
                  <GenderImg src={maleIcon} alt="" />
                  <GenderHint>男性</GenderHint>
                </GenderItem>
              </Col>
              <Col xs={11} className="ml-10">
                <GenderItem
                  onClick={() => setGender(USER_GENDER.FEMALE)}
                  className={
                    selectGender === `${USER_GENDER.FEMALE}` ? `selected` : ''
                  }
                >
                  <GenderImg src={femaleIcon} alt="" />
                  <GenderHint>女性</GenderHint>
                </GenderItem>
              </Col>
            </Row>
            {errors?.gender_id && (
              <p style={{ color: '#ff4d4f' }}>{errors.gender_id.message}</p>
            )}
          </Col>
        </Row>

        {isTelemedicine && (
          <ButtonCustom
            className="btn-default btn-chestnut-rose br-60 mt-30"
            loading={isSubmitting}
            htmlType="button"
            onClick={handleSubmit(handleFormSubmit)}
            name="次へ進む"
            showIconRight
          />
        )}
      </StyleForm>

      <BirthDateModal isOpen={birthDatePopup} onSubmit={setBirthDatePopup} />
      <GenderModal isOpen={genderPopup} onSubmit={setGenderPopup} />
      <BirthDateAGenderModal
        isOpen={birthDateAGenderPopup}
        onSubmit={setBirthDateAGenderPopup}
      />
      <DuplicateAccountModal
        open={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
      />
    </React.Fragment>
  )
}

const StyleForm = styled(Form)`
  .input-birthday {
    .aga-input {
      font-size: 16px;
    }
  }

  .picker-group {
    position: fixed;
    width: 100%;
    bottom: 0;
    left: 0;
    height: 100vh;
    background: #000000a8;
    z-index: 100;

    &__item {
      position: absolute;
      width: 100%;
      background-color: #fff;
      bottom: 0;
      left: 0;
      font-size: 16px;
      border-radius: 10px 10px 0 0;

      .birth-action {
        background-color: #ffffff;
        padding: 15px;
        border-bottom: 1px solid #998800;
        border-radius: 10px 10px 0 0;
        justify-content: space-between;

        .btn-warning {
          color: var(--color-dusty-gray);
          background-color: var(--color-white);
          cursor: pointer;
        }

        .btn-gray {
          color: #999999;
          cursor: pointer;
        }
      }
    }
  }

  .picker-date {
    background-color: #f1efe7;
    border: 1px solid #cfc392;
    padding: 10px;
  }

  .picker-inner {
    -webkit-mask-box-image-source: none !important;
  }

  .picker-container .picker-column .picker-item.picker-item-selected {
    font-weight: bold;
  }
`

const GenderItem = styled.div`
  display: flex;
  border: none;
  padding: 10px;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--color-pampas);

  &.selected {
    border-color: #980;
    background-color: #f1efe7;
    border: unset;
  }
`
const GenderHint = styled.p`
  height: 40px;
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 13px;
  margin-left: 10px;
`
const GenderImg = styled.img`
  width: 40px;
  height: 40px;
`

export default UserInfoForm
